import { DevRequestAction as A, DevRequestLane, DevRequestStatus as S, DevRequestWorker } from '../../models/devRequest';

// Espelha a tabela de transições do backend (spec "Pedidos ao dev", 6.1). O backend é quem valida; aqui só
// decide quais botões ficam ativos.
const TRANSITIONS: Record<A, S[] | 'any' | 'notFinal'> = {
  [A.send]: [S.pending],
  [A.answer]: [S.waitingSupport],
  [A.approve]: [S.waitingApproval],
  [A.reject]: [S.waitingApproval],
  [A.migrated]: [S.waitingMigration],
  [A.resolve]: [S.delivered],
  [A.reopen]: [S.delivered, S.resolved],
  [A.retry]: [S.failed],
  [A.cancel]: 'notFinal',
  [A.comment]: 'any',
};

export const allowedActions = (status: S): A[] =>
  (Object.keys(TRANSITIONS) as A[]).filter(action => {
    const from = TRANSITIONS[action];
    if (from === 'any') { return true; }
    if (from === 'notFinal') { return status !== S.resolved && status !== S.canceled; }
    return from.includes(status);
  });

/** Só o desenvolvedor faz, com digital a cada clique (rota /dev-actions do backend). */
export const DEV_ACTIONS: A[] = [A.approve, A.reject, A.migrated, A.retry];

/** Levam texto ao robô: só o computador liberado, com digital ou PIN a cada clique (mesma rota do backend). */
export const SENDER_ACTIONS: A[] = [A.send, A.answer, A.reopen];

export const SIGNED_ACTIONS: A[] = [...DEV_ACTIONS, ...SENDER_ACTIONS];

/** Pendente sem "Enviar ao robô" é rascunho: o robô não vê. */
export const isDraft = (r: { status: S; sentAt?: string | null; resumeReason?: string | null }): boolean =>
  r.status === S.pending && !r.sentAt && !r.resumeReason;

/** Só pending sem retomada aceita edição (o backend recusa o resto), e editar volta o pedido para rascunho. */
export const canEdit = (status: S, resumeReason?: string | null): boolean => status === S.pending && !resumeReason;

const MIN = 60_000;
const HOUR = 60 * MIN;

/** Fila muda por tempo sem sinal: urgente > 5 min ou normal > 2 h fica vermelho. Retomada não alarma. */
export const laneIsSilent = (lane: DevRequestLane, lastSeenAt: string | null | undefined, now: number): boolean => {
  const limit = lane === DevRequestLane.urgent ? 5 * MIN : lane === DevRequestLane.normal ? 2 * HOUR : null;
  if (limit === null) { return false; }
  const seen = lastSeenAt ? new Date(lastSeenAt).getTime() : NaN;
  return Number.isNaN(seen) || now - seen > limit;
};

export type LaneState = 'ok' | 'busy' | 'silent';

/** O worker manda heartbeat do pedido a cada 1 min: 10 min sem ele é Mac dormindo ou processo morto. */
export const laneState = (lane: DevRequestLane, worker: Pick<DevRequestWorker, 'lastSeenAt' | 'busy'> | undefined, now: number): LaneState => {
  if (worker?.busy) {
    const hb = worker.busy.heartbeatAt ? new Date(worker.busy.heartbeatAt).getTime() : NaN;
    return Number.isNaN(hb) || now - hb > 10 * MIN ? 'silent' : 'busy';
  }
  return laneIsSilent(lane, worker?.lastSeenAt, now) ? 'silent' : 'ok';
};

/** Tempo decorrido em segundos, minutos, horas ou dias (a unidade vem separada para o rótulo traduzir). */
export const elapsed = (iso: string | null | undefined, now: number): { n: number; unit: 's' | 'min' | 'h' | 'd' } | null => {
  const t = iso ? new Date(iso).getTime() : NaN;
  if (Number.isNaN(t)) { return null; }
  const sec = Math.max(0, Math.round((now - t) / 1000));
  if (sec < 60) { return { n: sec, unit: 's' }; }
  if (sec < 3600) { return { n: Math.floor(sec / 60), unit: 'min' }; }
  if (sec < 86400) { return { n: Math.floor(sec / 3600), unit: 'h' }; }
  return { n: Math.floor(sec / 86400), unit: 'd' };
};
